import { useLazyQuery, useQuery } from '@apollo/client'
import { RockpoolItemBuyerData, RockpoolItemBuyerVars, ROCKPOOL_ITEM_BUYER_QUERY } from '@graphql/nftfy/backend/rockpool/RockpoolItemBuyer'
import { RockpoolFilter } from '@graphql/variables/RockPoolVariables'
import { code } from '@messages'
import { notifyError } from '@services/NotificationService'
import { useEffect, useState } from 'react'
import { backendV2 } from '../../graphql/ClientGraphql'
import {
  PrivateRockpoolItemByIdData,
  PrivateRockpoolItemByIdVars,
  PRIVATE_ROCKPOOL_ITEM_BY_ID_QUERY
} from '../../graphql/nftfy/backend/rockpool/PrivateRockpoolItemByIdAndSeller'
import {
  PublicRockpoolItemByIdData,
  PublicRockpoolItemByIdVars,
  PUBLIC_ROCKPOOL_ITEM_BY_ID_QUERY
} from '../../graphql/nftfy/backend/rockpool/PublicRockpoolItemById'
import {
  RockpoolItemBuyersData,
  RockpoolItemBuyersVars,
  ROCKPOOL_ITEM_BUYERS_QUERY
} from '../../graphql/nftfy/backend/rockpool/RockpoolItemBuyers'
import {
  RockpoolPublicItemsData,
  RockpoolPublicItemsVars,
  ROCKPOOL_PUBLIC_ITEMS_QUERY
} from '../../graphql/nftfy/backend/rockpool/RockpoolPublicItemsQuery'
import { useGlobalConfig } from '../ConfigHook'

export const useRockpoolItemBuyer = (accountAddress: string | undefined, rockpoolItemId: string, chainId: number) => {
  const { loading, data, error, refetch } = useQuery<RockpoolItemBuyerData, RockpoolItemBuyerVars>(ROCKPOOL_ITEM_BUYER_QUERY, {
    variables: {
      accountAddress: accountAddress || '',
      rockpoolItemId,
      chainId
    },
    skip: !accountAddress,
    fetchPolicy: 'network-only',
    client: backendV2
  })

  return {
    loading,
    data,
    error,
    refetch
  }
}

export const useRockpoolItemBuyers = (chainId: number, rockpoolItemId: string) => {
  const { paginationLimit } = useGlobalConfig()
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const { loading, data, error, fetchMore, refetch } = useQuery<RockpoolItemBuyersData, RockpoolItemBuyersVars>(
    ROCKPOOL_ITEM_BUYERS_QUERY,
    {
      variables: {
        chainId,
        rockpoolItemId,
        limit: paginationLimit,
        offset
      },
      fetchPolicy: 'network-only',
      client: backendV2
    }
  )
  const loadMore = async () => {
    if (hasMore) {
      const isLoaded = (data?.rockpoolItemBuyers.length || 0) - paginationLimit >= paginationLimit
      const newOffset = offset + paginationLimit
      const nextPageResult = await fetchMore({
        variables: {
          offset: isLoaded ? data?.rockpoolItemBuyers.length || 0 : newOffset
        }
      })
      setOffset(newOffset)
      setHasMore(nextPageResult.data.rockpoolItemBuyers.length === paginationLimit)
    }
  }

  useEffect(() => {
    if (error) {
      throw error
    }
  }, [error])

  return {
    rockpoolItemBuyersLoading: loading,
    rockpoolItemBuyers: data?.rockpoolItemBuyers || [],
    rockpoolBuyersCount: data?.buyersCountRockpoolItems.buyersCount || 0,
    hasMore,
    loadMore,
    refetch
  }
}

export const useRockpoolDetail = () => {
  const GetPublicItemById = (rockpoolItemId: string, chainId: number) => {
    const [getPublicItemById, { loading, data }] = useLazyQuery<PublicRockpoolItemByIdData, PublicRockpoolItemByIdVars>(
      PUBLIC_ROCKPOOL_ITEM_BY_ID_QUERY,
      {
        variables: {
          rockpoolItemId,
          chainId
        },
        client: backendV2,
        pollInterval: 60 * 1000,
        fetchPolicy: 'network-only'
      }
    )
    return { getPublicItemById, loadingPublic: loading, rockpoolPublicItem: data?.publicRockpoolItem, GetPublicItemById }
  }
  const GetPrivateItemByIdAndCreator = (rockpoolItemId: string, chainId: number, creator: string) => {
    const [getPrivateItemByIdAndSeller, { loading, data }] = useLazyQuery<PrivateRockpoolItemByIdData, PrivateRockpoolItemByIdVars>(
      PRIVATE_ROCKPOOL_ITEM_BY_ID_QUERY,
      {
        variables: {
          rockpoolItemId,
          chainId,
          creator: creator || ''
        },
        client: backendV2,
        pollInterval: 60 * 1000,
        fetchPolicy: 'network-only'
      }
    )
    return { getPrivateItemByIdAndSeller, loadingPrivate: loading, rockpoolPrivateItem: data?.privateRockpoolItem }
  }

  return { GetPublicItemById, GetPrivateItemByIdAndCreator }
}

export const useRockpoolPublicItems = (chainId: number, filters: RockpoolFilter) => {
  const { paginationLimit } = useGlobalConfig()
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)

  const { loading, data, error, fetchMore } = useQuery<RockpoolPublicItemsData, RockpoolPublicItemsVars>(ROCKPOOL_PUBLIC_ITEMS_QUERY, {
    client: backendV2,
    variables: {
      chainId,
      limit: paginationLimit,
      filterBy: filters.type,
      offset,
      orderBy: filters.sortingField,
      orderDirection: filters.sortingDirection
    }
  })

  const loadMore = async () => {
    if (hasMore && !loading) {
      const isLoaded = (data?.publicRockpoolItems.length || 0) - paginationLimit >= paginationLimit
      const newOffset = offset + paginationLimit
      try {
        const { data: nextPageData, error: nextPageError } = await fetchMore({
          variables: {
            offset: isLoaded ? data?.publicRockpoolItems.length || 0 : newOffset
          }
        })

        if (nextPageError) {
          notifyError(code[5015])
          return
        }

        setOffset(newOffset)
        setHasMore(nextPageData.publicRockpoolItems.length === paginationLimit)
      } catch (e) {
        notifyError(code[5015])
        return
      }
    }

    if (!data?.publicRockpoolItems.length) {
      setHasMore(false)
    }
  }

  useEffect(() => {
    setOffset(0)
    setHasMore(true)
  }, [chainId, filters])

  if (error) {
    notifyError(code[5015])
    return { rockpoolItems: [], loading, loadMore, hasMore }
  }

  return { rockpoolItems: data?.publicRockpoolItems || [], loading, loadMore, hasMore }
}
